/**
 * CroArrPacingTile — CRO-01 (New ARR YTD) "how much is left" + 2025 pacing chart.
 *
 * Phase 04.1 #5 (2026-07-09). Replaces the CRO-01 big-number tile with a
 * cumulative-ARR line chart per John's chosen design:
 *   - current-FY actual (solid brand-blue, stops at the current month)
 *   - straight-line on-pace to the annual target (dashed grey)
 *   - prior-FY (2025) actual cumulative (muted violet) — the linearity overlay
 *   - target markLine + a "remaining to target" callout
 *
 * Reuses the CroTile/CroTrendTile 4-state machine (loading / cache-warming /
 * SF-unreachable / success) and the CRO-01 definition for title + tooltip.
 */
import ReactECharts from 'echarts-for-react';

import {
  CacheNotReadyError,
  SalesforceUnavailableError,
} from '../lib/api-client.js';
import { getCroMetricDefinition } from '../lib/definitions.js';
import { formatCurrency } from '../lib/format.js';

import { SfUnreachableBanner } from './SfUnreachableBanner.js';
import { Card, CardContent, CardHeader, CardTitle } from './ui/Card.js';
import { Skeleton } from './ui/Skeleton.js';
import { Tooltip } from './ui/Tooltip.js';

import type { CroArrPacingResponse } from '@saledash/shared';

interface CroArrPacingTileProps {
  data: CroArrPacingResponse | undefined;
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  refetch: () => void;
}

/** Build the ECharts option. Returns `object` so TS does not type-check against
 *  EChartsOption (matches CroTrendTile's pattern; keeps markLine.type: 'dashed'
 *  free of the ZRLineType friction seen in the Main echarts-builder). */
function buildPacingOption(data: CroArrPacingResponse): object {
  const money = (v: number): string => formatCurrency(v);

  const targetMark =
    data.target_value !== null
      ? {
          markLine: {
            silent: true,
            symbol: 'none',
            lineStyle: { color: '#d97706', width: 1, type: 'dashed' },
            data: [{ yAxis: data.target_value, name: 'Target' }],
            label: {
              show: true,
              position: 'insideEndTop',
              formatter: `Target ${money(data.target_value)}`,
              fontSize: 9,
              color: '#d97706',
            },
          },
        }
      : {};

  return {
    animation: false,
    grid: { left: 46, right: 16, top: 26, bottom: 24 },
    legend: {
      show: true,
      top: 0,
      right: 0,
      icon: 'roundRect',
      itemWidth: 10,
      itemHeight: 4,
      textStyle: { fontSize: 10, color: '#6b7280' },
    },
    tooltip: {
      trigger: 'axis',
      backgroundColor: 'rgba(17,24,39,0.9)',
      borderColor: 'transparent',
      textStyle: { color: '#fff', fontSize: 11 },
      formatter: (
        params: { seriesName: string; value: number | null; axisValue: string }[],
      ) => {
        const head = params[0]?.axisValue ?? '';
        const lines = params
          .filter((p) => p.value !== null)
          .map((p) => `${p.seriesName}: <strong>${money(Number(p.value))}</strong>`);
        return `${head}<br/>${lines.join('<br/>')}`;
      },
    },
    xAxis: {
      type: 'category',
      data: data.months,
      boundaryGap: false,
      axisLabel: { fontSize: 9, color: '#6b7280' },
      axisLine: { lineStyle: { color: '#e5e7eb' } },
      axisTick: { show: false },
    },
    yAxis: {
      type: 'value',
      axisLabel: { fontSize: 9, color: '#6b7280', formatter: (v: number) => money(v) },
      splitLine: { lineStyle: { color: '#f3f4f6' } },
    },
    series: [
      {
        name: `${String(data.fiscal_year)} actual`,
        type: 'line',
        data: data.cy_cumulative,
        connectNulls: false,
        smooth: false,
        symbol: 'circle',
        symbolSize: 5,
        lineStyle: { color: '#2851e6', width: 2.5 },
        itemStyle: { color: '#2851e6' },
        areaStyle: {
          color: {
            type: 'linear',
            x: 0,
            y: 0,
            x2: 0,
            y2: 1,
            colorStops: [
              { offset: 0, color: 'rgba(40,81,230,0.18)' },
              { offset: 1, color: 'rgba(40,81,230,0)' },
            ],
          },
        },
        z: 3,
        ...targetMark,
      },
      {
        name: 'On-pace',
        type: 'line',
        data: data.on_pace,
        smooth: false,
        symbol: 'none',
        lineStyle: { color: '#9ca3af', width: 1.5, type: 'dashed' },
        z: 2,
      },
      {
        name: `${String(data.prior_fiscal_year)} actual`,
        type: 'line',
        data: data.py_cumulative,
        smooth: false,
        symbol: 'none',
        lineStyle: { color: '#c4b5fd', width: 2 },
        itemStyle: { color: '#c4b5fd' },
        z: 1,
      },
    ],
  };
}

export function CroArrPacingTile({
  data,
  isLoading,
  isError,
  error,
  refetch,
}: CroArrPacingTileProps): React.JSX.Element {
  const definition = getCroMetricDefinition('CRO-01');
  const tooltipContent = (
    <>
      <p className="mb-1 font-semibold text-ink-900">{definition.name}</p>
      <p>{definition.formula_plain_english}</p>
      <p className="mt-2 text-[10px] uppercase tracking-wide text-ink-500">
        Source: {definition.source_of_truth_system}
      </p>
    </>
  );

  const header = (
    <CardHeader>
      <CardTitle className="text-[0.72rem] uppercase tracking-wider font-semibold text-ink-600">
        Closed Won New ARR
        {data ? ` — FY${String(data.fiscal_year)}` : ''}
      </CardTitle>
      <Tooltip trigger="?" label="Show definition">{tooltipContent}</Tooltip>
    </CardHeader>
  );

  if (isError && error instanceof CacheNotReadyError) {
    return (
      <Card data-testid="cro-tile-CRO-01">
        {header}
        <CardContent>
          <div
            role="status"
            className="flex items-start gap-3 rounded-lg border border-warn bg-warn-bg px-4 py-3 text-sm text-warn-text"
            data-testid="cache-warming-banner"
          >
            <span aria-hidden className="mt-0.5 inline-flex h-5 w-5 flex-none items-center justify-center rounded-full bg-warn text-xs font-bold text-white">⌛</span>
            <div className="flex-1">
              <p className="font-semibold">Pacing chart warming up</p>
              <p className="mt-1 text-xs text-warn-text/80">First ARR-by-month refresh expected within ~7 minutes.</p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (isError && error instanceof SalesforceUnavailableError) {
    return (
      <Card data-testid="cro-tile-CRO-01">
        {header}
        <CardContent><SfUnreachableBanner onRetry={refetch} /></CardContent>
      </Card>
    );
  }

  if (isLoading || !data) {
    return (
      <Card data-testid="cro-tile-CRO-01">
        {header}
        <CardContent>
          <div className="space-y-3">
            <Skeleton className="h-9 w-40" />
            <Skeleton className="h-[220px] w-full" />
          </div>
        </CardContent>
      </Card>
    );
  }

  const pct =
    data.target_value !== null && data.target_value > 0
      ? Math.round((data.current_value / data.target_value) * 100)
      : null;

  return (
    <Card data-testid="cro-tile-CRO-01">
      {header}
      <CardContent>
        {/* Headline: current YTD + remaining-to-target callout */}
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <p className="text-3xl font-bold tabular-nums text-ink-900" data-testid="kpi-value">
            {formatCurrency(data.current_value)}
          </p>
          {data.remaining !== null && (
            <span className="text-sm text-ink-600" data-testid="remaining-callout">
              <span className="font-semibold tabular-nums text-ink-900">{formatCurrency(data.remaining)}</span> left to target
              {pct !== null ? ` · ${String(pct)}%` : ''}
            </span>
          )}
        </div>

        {/* Cumulative pacing chart: 2026 actual vs on-pace vs 2025 actual */}
        <div className="mt-3 h-[220px] w-full" data-testid="pacing-chart">
          <ReactECharts
            option={buildPacingOption(data)}
            style={{ height: '100%', width: '100%' }}
            opts={{ renderer: 'canvas' }}
            notMerge
            lazyUpdate={false}
          />
        </div>
      </CardContent>
    </Card>
  );
}
